
import React from 'react';
import { BuildingType, Grid } from '../../types';
import { GRID_SIZE } from '../../constants';
import { gridToWorld } from '../../utils/mapUtils';

const PipelineMesh = React.memo(({ x, y, grid, yOffset, isConnected }: { x: number; y: number; grid: Grid; yOffset: number; isConnected: boolean }) => {
  const pipeColor = isConnected ? '#64748b' : '#334155';
  const hasUp = y > 0 && grid[y - 1][x].buildingType === BuildingType.Pipeline;
  const hasDown = y < GRID_SIZE - 1 && grid[y + 1][x].buildingType === BuildingType.Pipeline;
  const hasLeft = x > 0 && grid[y][x - 1].buildingType === BuildingType.Pipeline;
  const hasRight = x < GRID_SIZE - 1 && grid[y][x + 1].buildingType === BuildingType.Pipeline;
  
  const Segment = ({r}: {r: any}) => <group rotation={r}><mesh position={[0,0,0]}><boxGeometry args={[0.3, 0.15, 1]} /><meshStandardMaterial color={pipeColor} /></mesh></group>;

  return (
    <group position={[0, yOffset + 0.08, 0]}>
      <mesh><boxGeometry args={[0.35, 0.16, 0.35]} /><meshStandardMaterial color={pipeColor} /></mesh>
      {hasUp && <group position={[0,0,-0.5]}><Segment r={[0,0,0]} /></group>}
      {hasDown && <group position={[0,0,0.5]}><Segment r={[0,0,0]} /></group>}
      {hasLeft && <group position={[-0.5,0,0]}><Segment r={[0,Math.PI/2,0]} /></group>}
      {hasRight && <group position={[0.5,0,0]}><Segment r={[0,Math.PI/2,0]} /></group>}
    </group>
  );
});

const RoadMesh = React.memo(({ x, y, grid }: { x: number; y: number; grid: Grid }) => {
    const hasUp = y > 0 && grid[y - 1][x].buildingType === BuildingType.Road;
    const hasDown = y < GRID_SIZE - 1 && grid[y + 1][x].buildingType === BuildingType.Road;
    const hasLeft = x > 0 && grid[y][x - 1].buildingType === BuildingType.Road;
    const hasRight = x < GRID_SIZE - 1 && grid[y][x + 1].buildingType === BuildingType.Road;

    const Lane = ({r}: {r: any}) => (
        <group rotation={r}>
            <mesh position={[0, 0.11, 0.25]} rotation={[-Math.PI/2, 0, 0]}>
                <planeGeometry args={[0.08, 0.5]} />
                <meshStandardMaterial color="#fbbf24" />
            </mesh>
        </group>
    );

    return (
        <group>
            {/* Center Marking if connected 3+ ways or intersections */}
            {(hasUp || hasDown || hasLeft || hasRight) && (
                 <mesh position={[0, 0.11, 0]} rotation={[-Math.PI/2, 0, 0]}>
                    <planeGeometry args={[0.15, 0.15]} />
                    <meshStandardMaterial color="#fbbf24" />
                </mesh>
            )}
            
            {hasUp && <Lane r={[0,0,0]} />}
            {hasDown && <Lane r={[0,Math.PI,0]} />}
            {hasLeft && <Lane r={[0,Math.PI/2,0]} />}
            {hasRight && <Lane r={[0,-Math.PI/2,0]} />}
        </group>
    );
});

export const GroundTile = React.memo(({ type, x, y, grid, isConnected, onHover, onLeave, onClick }: any) => {
  const [wx, _, wz] = gridToWorld(x, y);
  const color = type === BuildingType.Pipeline ? '#0f172a' : (type === BuildingType.None ? '#1e293b' : (type === BuildingType.Road ? '#334155' : '#334155'));

  return (
    <group position={[wx, 0, wz]}>
      <mesh position={[0, -0.1, 0]} receiveShadow onPointerEnter={() => onHover(x,y)} onPointerOut={onLeave} onPointerDown={(e) => { e.stopPropagation(); onClick(x, y); }}>
        <boxGeometry args={[0.95, 0.2, 0.95]} />
        <meshStandardMaterial color={color} roughness={0.5} />
      </mesh>
      {type === BuildingType.Pipeline && <PipelineMesh x={x} y={y} grid={grid} yOffset={0} isConnected={isConnected} />}
      {type === BuildingType.Road && <RoadMesh x={x} y={y} grid={grid} />}
    </group>
  );
});
